import { useCallback, useMemo } from "react";
import type { Job } from "../../../../../../../models";
import type { WizardIssues, WizardStep } from "../../WizardContainer/WizardContainer.types";
import { WIZARD_STEP_TITLES } from "../../WizardContainer/WizardContainer.types";
import type { SummaryProps, SummaryViewModel, SkippedIssueInfo, SkippedJobInfo } from "./Summary.types";

// ============================================================================
// Constants
// ============================================================================

/** Maps each WizardIssues key to its corresponding WizardStep (ordered to match wizard sidebar) */
const ISSUE_KEY_TO_STEP: Record<keyof WizardIssues, WizardStep> = {
	unmatched: "unmatched-features",
	duplicateBoundaries: "duplicate-boundaries",
	invalidData: "missing-invalid-data",
	overlaps: "overlapping-boundaries",
	overlappingZones: "overlapping-zones",
	overlappingPoints: "overlapping-points",
};

// ============================================================================
// Helpers
// ============================================================================

/** Extracts structured grower/farm/field info from a job */
function extractJobInfo(job: Job): SkippedJobInfo {
	return {
		grower: job.growerName || job.importGrowerName || "",
		farm: job.farmName || job.importFarmName || "",
		field: job.fieldName || job.importFieldName || "",
	};
}

/**
 * Builds a list of skipped issue info from the current issues and skippedIssueIds.
 * Pure function — no hooks, easy to test.
 */
export function buildSkippedIssueList(issues: WizardIssues, skippedIssueIds: Set<string>): SkippedIssueInfo[] {
	const result: SkippedIssueInfo[] = [];

	for (const [key, step] of Object.entries(ISSUE_KEY_TO_STEP) as [keyof WizardIssues, WizardStep][]) {
		const issueArray = issues[key];
		for (let i = 0; i < issueArray.length; i++) {
			const issue = issueArray[i];
			if (!skippedIssueIds.has(issue.id)) continue;

			// Extract job info depending on issue shape
			let jobs: SkippedJobInfo[];
			if ("jobs" in issue && Array.isArray(issue.jobs)) {
				jobs = issue.jobs.map(extractJobInfo);
			} else if ("job" in issue && issue.job) {
				jobs = [extractJobInfo(issue.job as Job)];
			} else {
				jobs = [];
			}

			result.push({
				issueId: issue.id,
				step,
				stepTitle: WIZARD_STEP_TITLES[step],
				issueIndex: i,
				jobs,
			});
		}
	}

	return result;
}

// ============================================================================
// ViewModel Hook
// ============================================================================

export function useSummaryViewModel(props: SummaryProps): SummaryViewModel {
	const { jobs, issues, skippedIssueIds, onNavigateToIssue } = props;

	const totalIssues =
		issues.overlaps.length +
		issues.duplicateBoundaries.length +
		issues.overlappingPoints.length +
		issues.overlappingZones.length +
		issues.unmatched.length +
		issues.invalidData.length;

	const skippedIssueList = useMemo(() => buildSkippedIssueList(issues, skippedIssueIds), [issues, skippedIssueIds]);

	const skippedCount = skippedIssueList.length;
	const remainingIssues = totalIssues - skippedCount;

	const jobsByGrower = useMemo(
		() =>
			jobs.reduce(
				(acc, job) => {
					const growerName = job.growerName || job.importGrowerName || "Unknown Grower";
					if (!acc[growerName]) {
						acc[growerName] = [];
					}
					acc[growerName].push(job);
					return acc;
				},
				{} as Record<string, Job[]>,
			),
		[jobs],
	);

	const handleIssueClick = useCallback(
		(item: SkippedIssueInfo) => {
			onNavigateToIssue?.(item.step, item.issueIndex);
		},
		[onNavigateToIssue],
	);

	return {
		jobCount: jobs.length,
		skippedCount,
		remainingIssues,
		jobsByGrower,
		skippedIssueList,
		handleIssueClick,
	};
}
